import {
	CommandButtonCompat as CommandButton,
	IButtonStyles,
	IIconProps,
	INavLink,
	INavLinkGroup,
	INavStyles,
	ISeparatorStyles,
	IStackItemStyles,
	IStackStyles,
	mergeStyles,
	mergeStyleSets,
	NavCompat as Nav,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	Theme,
	useTheme,
} from 'components/FluentUICompat';
import {useUserContext} from 'authentication/UserContext';
import {
	SidebarInfo,
	useSidebarInfoAndValidateIt,
} from 'context/SidebarInfoProvider';
import {getSideNavigationNode} from 'helpers';
import * as React from 'react';
import {TFunction, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {SideNavigationContext} from './SideNavigationProvider';
import {resolveNavSection} from './navSection';
import {PAGE_PATHS} from 'pages';
import {isDevEnvironment, isTestEnvironment} from '../Navigation';

const createLink = (name: string, url: string, key: string, icon: string) => ({
	name,
	url,
	key,
	icon,
});

type UserRoles = {
	isAdmin: boolean;
	isVko: boolean;
	isVex: boolean;
	isVkoExternal: boolean;
	isApprover: boolean;
	isReader: boolean;
	isBedarfskoordinator: boolean;
	isRegulationReader: boolean;
	isAvk: boolean;
	isBav: boolean;
};

type NavItemConfig = {
	name: string;
	url: string;
	key: string;
	icon: string;
	style?: Record<string, unknown>;
	expandAriaLabel?: string;
	links?: Array<{
		key: string;
		name: string;
		url: string;
		icon: string;
		style?: Record<string, unknown>;
	}>;
	allowedRoles: (keyof UserRoles)[];
};

// Role constants for each navigation item
const DASHBOARD_ROLES: (keyof UserRoles)[] = [];

const FAVORITES_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isApprover',
	'isReader',
	'isBedarfskoordinator',
	'isRegulationReader',
	'isAvk',
	'isBav',
];

const RECENTS_ROLES: (keyof UserRoles)[] = [];

const SUBSCRIPTIONS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isVkoExternal',
	'isApprover',
	'isReader',
	'isRegulationReader',
	'isBedarfskoordinator',
	'isAvk',
	'isBav',
];

const REGULATIONS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isVkoExternal',
	'isApprover',
	'isReader',
	'isBedarfskoordinator',
	'isRegulationReader',
	'isAvk',
	'isBav',
];

const POGIS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isApprover',
	'isReader',
	'isRegulationReader',
	'isBedarfskoordinator',
];

const REQUIREMENTS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isApprover',
	'isReader',
	'isBedarfskoordinator',
];

const VEHICLE_PROJECTS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isApprover',
	'isReader',
	'isBedarfskoordinator',
];

const VKO_VEX_LIST_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isReader',
	'isAvk',
	'isBav',
	'isBedarfskoordinator',
	'isApprover',
];

const SIMULATIONS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isBedarfskoordinator',
];

const REPORTS_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isApprover',
	'isReader',
	'isBedarfskoordinator',
	'isRegulationReader',
];

const GETEX_REGULATION_ROLES: (keyof UserRoles)[] = [
	'isAdmin',
	'isVko',
	'isVex',
	'isApprover',
	'isBedarfskoordinator',
];

const hasAnyRole = (
	roles: UserRoles,
	allowedRoles: (keyof UserRoles)[],
): boolean =>
	allowedRoles.length === 0 || allowedRoles.some(role => roles[role]);

const buildNavItems = (t: TFunction): NavItemConfig[] => [
	{
		name: t('Dashboard'),
		url: '/',
		key: 'home',
		icon: 'Home',
		allowedRoles: DASHBOARD_ROLES,
	},
	{
		name: t('Favorites'),
		url: '/favorites',
		key: 'favorites',
		icon: 'FavoriteStar',
		style: {paddingLeft: 10},
		allowedRoles: FAVORITES_ROLES,
	},
	{
		name: t('Recents'),
		url: '/recents',
		key: 'recents',
		icon: 'Clock',
		style: {paddingLeft: 10},
		allowedRoles: RECENTS_ROLES,
	},
	{
		name: t('Subscriptions'),
		url: '/subscriptions',
		key: 'subscriptions',
		icon: 'Subscribe',
		style: {paddingLeft: 10},
		allowedRoles: SUBSCRIPTIONS_ROLES,
	},
	{
		name: t('CoPilot'),
		url: '/copilotIntegration',
		key: 'copilotIntegration',
		icon: 'Diamond',
		style: {background: '#edb5b5'},
		allowedRoles: REGULATIONS_ROLES,
	},
	{
		name: t('Regulations'),
		url: '/regulations',
		key: 'regulations',
		icon: 'EntitlementRedemption',
		allowedRoles: REGULATIONS_ROLES,
	},
	...(isDevEnvironment() || isTestEnvironment()
		? [
				{
					name: t('GetexRegulations'),
					url: '/getexRegulations',
					key: 'getexRegulations',
					icon: 'ComplianceAudit',
					allowedRoles: GETEX_REGULATION_ROLES,
				},
		  ]
		: []),
	{
		name: t('Pogis'),
		url: '/pogisArchive',
		key: 'pogisArchive',
		icon: 'Archive',
		allowedRoles: POGIS_ROLES,
	},
	{
		name: t('Requirements'),
		url: '/requirements',
		key: 'requirements',
		icon: 'Quantity',
		allowedRoles: REQUIREMENTS_ROLES,
	},
	{
		name: t('VehicleProjects'),
		url: '/vehicleProjects',
		key: 'vehicleProjects',
		icon: 'ProjectCollection',
		allowedRoles: VEHICLE_PROJECTS_ROLES,
	},
	{
		name: t('VkoVexList'),
		url: '/VkoVexList',
		key: 'VkoVexList',
		icon: 'EntitlementRedemption',
		allowedRoles: VKO_VEX_LIST_ROLES,
	},
	{
		name: t('Simulations'),
		url: PAGE_PATHS.simulations,
		key: 'simulations',
		icon: 'TableComputed',
		allowedRoles: SIMULATIONS_ROLES,
	},
	{
		name: t('Reports'),
		url: '/reports/regulatory-documents',
		key: 'reports-regulatory-documents',
		icon: 'BookAnswers',
		expandAriaLabel: t('ExpandReports'),
		links: [
			{
				key: 'reports-regulatory-documents',
				name: t('RegulatoryDocuments'),
				url: '/reports/regulatory-documents',
				icon: 'Table',
				style: {paddingLeft: 20},
			},
		],
		allowedRoles: REPORTS_ROLES,
	},
];

const getNavLinkGroups = (t: TFunction, roles: UserRoles): INavLinkGroup[] => {
	const items = buildNavItems(t);
	const filteredLinks = items
		.filter(item => hasAnyRole(roles, item.allowedRoles))
		.map(({allowedRoles: _allowedRoles, ...link}) => link);

	return [{links: filteredLinks}] as INavLinkGroup[];
};

const getAdminNavLinkGroups = (t: TFunction) =>
	[
		{
			links: [
				createLink(
					// Despite the underlying entity is markets, we call it Legal Area
					t('Markets'),
					'/admin/markets',
					'markets',
					'WebAppBuilderFragment',
				),
				createLink(
					t('RegulationsClusters'),
					'/admin/regulationsClusters',
					'regulationsClusters',
					'EntitlementPolicy',
				),
				createLink(
					t('VehicleCategories'),
					'/admin/vehicleCategories',
					'vehicleCategories',
					'Car',
				),
				createLink(
					t('DriveVariants'),
					'/admin/driveVariants',
					'driveVariants',
					'EngineeringGroup',
				),
				createLink(
					t('EngineVariants'),
					'/admin/engineVariants',
					'engineVariants',
					'Robot',
				),
				createLink(
					t('GearboxVariants'),
					'/admin/gearboxVariants',
					'gearboxVariants',
					'Settings',
				),
				getSideNavigationNode(
					t('BodyworkVariants'),
					'/admin/bodyworkVariants',
					'bodyworkVariants',
					'WebAppBuilderSlot',
				),
				createLink(
					t('ActiveTypeMarkets'),
					'/admin/activeTypeMarkets',
					'activeTypeMarkets',
					'Market',
				),
				createLink(
					t('Categories'),
					'/admin/categories',
					'categories',
					'StackIndicator',
				),
				createLink(
					t('Keywords'),
					'/admin/keywords',
					'keywords',
					'AzureKeyVault',
				),
				createLink(t('VKO'), '/admin/vkos', 'vkos', 'Group'),
				createLink(
					t('VexClusters'),
					'/admin/vexClusters',
					'vexClusters',
					'Group',
				),
				createLink(t('Tags'), '/admin/tags', 'tags', 'Tag'),
				createLink(
					t('PcmsClusters'),
					'/admin/pcmsClusters',
					'pcmsClusters',
					'ProductionFloorManagement',
				),
				createLink(
					t('SystemLevels'),
					'/admin/systemLevels',
					'systemLevels',
					'System',
				),
				createLink(
					t('AttachmentCategories'),
					'/admin/attachmentCategories',
					'attachmentCategories',
					'Attach',
				),
				createLink(
					t('DataProtection'),
					'/admin/dataProtection',
					'dataProtection',
					'Shield',
				),
				createLink(
					t('References'),
					'/admin/references',
					'references',
					'DataflowsLink',
				),
				createLink(
					t('DocumentSources'),
					'/admin/documentSources',
					'documentSources',
					'DocumentSet',
				),
				createLink(t('Countries'), '/admin/countries', 'countries', 'Flag'),
				createLink(
					t('Notifications'),
					'/admin/notifications',
					'notifications',
					'MailAlert',
				),
				createLink(
					t('SubscriptionAllocation'),
					'/admin/distributionListSubscriptions',
					'distributionListSubscriptions',
					'Subscribe',
				),
				createLink(
					t('Permissions'),
					'/admin/permissions',
					'permissions',
					'Permissions',
				),
				createLink(
					t('Announcements'),
					'/admin/announcements',
					'announcements',
					'Ringer',
				),
			],
		},
	] as INavLinkGroup[];

const sidebarHeight = 'calc(100vh - 70px)';

export const SideNavigation: React.FunctionComponent = () => {
	const {t} = useTranslation('appShell/sidenavigation', {
		keyPrefix: 'SideNavigation',
	});

	const {
		isAdmin,
		isVko,
		isVex,
		isVkoExternal,
		isApprover,
		isReader,
		isBedarfskoordinator,
		isRegulationReader,
		isAvk,
		isBav,
	} = useUserContext();

	const roles: UserRoles = React.useMemo(
		() => ({
			isAdmin,
			isVko,
			isVex,
			isVkoExternal,
			isApprover,
			isReader,
			isBedarfskoordinator,
			isRegulationReader,
			isAvk,
			isBav,
		}),
		[
			isAdmin,
			isVko,
			isVex,
			isVkoExternal,
			isApprover,
			isReader,
			isBedarfskoordinator,
			isRegulationReader,
			isAvk,
			isBav,
		],
	);

	const adminNav = React.useMemo(() => getAdminNavLinkGroups(t), []);
	const userNav = React.useMemo(() => getNavLinkGroups(t, roles), [roles]);

	const theme = useTheme();
	const {
		stackStyles,
		stackItemStyles,
		activeButtonStyles,
		activeStyles,
		activeStylesCollapsed,
		stackStylesCollapsed,
		collapseNavStyles,
		enhanceNavStyles,
		collapseButtonStyles,
		separatorStyles,
		buttonStyles,
	} = getStyles(theme);

	const [selected, setSelected] = React.useState<string>();

	const {isOpen, setIsOpen}: SidebarInfo = useSidebarInfoAndValidateIt(
		SideNavigationContext,
	);

	const collapsed = !isOpen;

	const setCollapsed = React.useCallback(
		(isCollapsed: boolean): void => {
			setIsOpen(!isCollapsed);
		},
		[setIsOpen],
	);

	const [active, setActive] = React.useState(
		localStorage.getItem('sidenavigation') ?? 'User',
	);
	const [sideNav, setSideNav] = React.useState(userNav);

	React.useEffect(() => {
		// Correct a stored 'Admin' section the current (narrowed) role can no longer access.
		const section = resolveNavSection(active, isAdmin);
		if (section !== active) {
			setActive(section);
			localStorage.setItem('sidenavigation', section);
		}
		setSideNav(section === 'Admin' ? adminNav : userNav);
	}, [roles]);

	const adminClick = React.useCallback(() => {
		setSideNav(adminNav);
		setActive('Admin');
		localStorage.setItem('sidenavigation', 'Admin');
	}, [adminNav, active]);

	const userClick = React.useCallback(() => {
		setSideNav(userNav);
		setActive('User');
		localStorage.setItem('sidenavigation', 'User');
	}, [userNav, active]);

	const toggleCollapsed = React.useMemo(
		() => () => {
			setCollapsed(!collapsed);
			localStorage.setItem('navigation-drawer', `${!collapsed}`);
		},
		[collapsed, setCollapsed],
	);

	const navigate = useNavigate();

	const _onLinkClick = React.useCallback(
		(ev?: React.MouseEvent<HTMLElement>, item?: INavLink) => {
			ev?.preventDefault();

			navigate(item?.url ?? '/');
			setSelected(item?.key ?? '');
		},
		[setSelected, navigate],
	);

	const commandBtnHeight = '2.5rem';

	const calculateContainerHeight = React.useCallback(
		(buttonsHeight: string, extraHeight: string): string => {
			const expandBtnHeight = '2.5rem';
			/**
			 * We must calculate this leave enough room for the admin's buttons at the bottom of the
			 * sidebar.
			 */
			return `calc(${sidebarHeight} - ${buttonsHeight} - ${expandBtnHeight} + ${extraHeight})`;
		},
		[],
	);

	const getCollapsedContainerHeight = React.useCallback((): string => {
		const buttonsHeight = `calc(${commandBtnHeight} * 2)`;
		/**
		 * We add a certain amount to eliminate the extra margin at the bottom,
		 * which is caused by navigation.
		 */
		return calculateContainerHeight(buttonsHeight, '1.5rem');
	}, [calculateContainerHeight]);

	const getContainerHeight = React.useCallback((): string => {
		if (collapsed) return getCollapsedContainerHeight();
		return calculateContainerHeight(commandBtnHeight, '1rem');
	}, [collapsed, calculateContainerHeight, getCollapsedContainerHeight]);

	const classNames = React.useMemo(() => {
		return mergeStyleSets({
			container: {
				overflowY: 'auto',
				height: getContainerHeight(),
			},
		});
	}, [getContainerHeight]);

	const adminIcon: IIconProps = {iconName: 'AdminALogoInverse32'};
	const userIcon: IIconProps = {iconName: 'AccountBrowser'};
	const enhanceIcon: IIconProps = {iconName: 'DoubleChevronRight'};
	const collapseIcon: IIconProps = {iconName: 'DoubleChevronLeft'};

	return (
		<Stack styles={collapsed ? stackStylesCollapsed : stackStyles}>
			<CommandButton
				iconProps={collapsed ? enhanceIcon : collapseIcon}
				onClick={toggleCollapsed}
				styles={collapseButtonStyles}
				ariaLabel={'collapse'}
			/>
			<Separator styles={separatorStyles} />
			<div className={classNames.container}>
				<Nav
					onLinkClick={_onLinkClick}
					selectedKey={selected}
					ariaLabel='Porsche Project Nav'
					groups={sideNav}
					styles={collapsed ? collapseNavStyles : enhanceNavStyles}
				/>
			</div>
			{isAdmin && (
				<Stack styles={stackItemStyles}>
					<CommandButton
						aria-label='User'
						onClick={userClick}
						iconProps={userIcon}
						styles={active === 'User' ? buttonStyles : activeButtonStyles}
						className={collapsed ? activeStylesCollapsed : activeStyles}
					/>

					<CommandButton
						aria-label='Admin'
						onClick={adminClick}
						iconProps={adminIcon}
						styles={active === 'Admin' ? buttonStyles : activeButtonStyles}
						className={collapsed ? activeStylesCollapsed : activeStyles}
					/>
				</Stack>
			)}
		</Stack>
	);
};

const getStyles = (theme: Theme) => ({
	stackStyles: {
		root: {
			width: '240px',
			height: sidebarHeight,
			position: 'relative',
			background: theme.palette.white,
			boxSizing: 'border-box',
			borderRight: `1px solid ${theme.palette.neutralLight}`,
		},
	} as IStackStyles,
	stackStylesCollapsed: {
		root: {
			height: sidebarHeight,
			position: 'relative',
			background: theme.palette.white,
			boxSizing: 'border-box',
			borderRight: `1px solid ${theme.palette.neutralLight}`,
		},
	} as IStackStyles,
	stackItemStyles: {
		root: {
			position: 'absolute',
			bottom: 0,
			display: 'flex',
			alignItems: 'center',
			flexFlow: 'wrap',
			width: '100%',
		},
	} as IStackItemStyles,
	buttonStyles: {
		root: {background: theme.palette.white},
	} as IButtonStyles,
	activeButtonStyles: {
		root: {
			background: theme.palette.neutralLight,
		},
		icon: {
			color: theme.palette.neutralDark,
		},
		iconHovered: {
			color: theme.palette.neutralDark,
		},
	} as IButtonStyles,
	collapseButtonStyles: {
		root: {
			paddingLeft: '10px',
			selectors: {
				':hover': {
					backgroundColor: theme.palette.neutralLight,
				},
			},
		},
	} as IButtonStyles,
	collapseNavStyles: {
		linkText: {
			display: 'none',
		},
		link: {
			paddingLeft: '10px',
			paddingRight: '10px',
		},
	} as INavStyles,
	enhanceNavStyles: {
		linkText: {
			display: 'flex',
		},
		link: {
			paddingLeft: '10px',
			paddingRight: '10px',
		},
		chevronButton: {
			left: 'auto',
			right: 4,
		},
	} as INavStyles,
	separatorStyles: {
		root: {
			height: '8px',
			padding: '0px',
		},
	} as ISeparatorStyles,
	activeStyles: mergeStyles({
		width: '50%',
		display: 'flex',
		justifyContent: 'center',
	}),
	activeStylesCollapsed: mergeStyles({
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
	}),
});
